/* ──────────────────────────────────────────────────────────────
   src/pages/reports/reservas/HeatmapHorarioReservasPage.tsx
   ────────────────────────────────────────────────────────────── */
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import {
  Stack,
  Typography,
  IconButton,
  Tooltip,
  Paper,
  CircularProgress,
  Alert,
  Box,
  Button,
} from "@mui/material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { useTranslation } from "react-i18next";
import { useHeatmapHorarioReservasReport } from "../../../hooks/useReportJob";
import type { HeatHorarioResRow } from "../../../api/reports";
import { downloadReportFile } from "../../../components/utils/downloadXlsx";

/* ─── malla 7 × 14 ──────────────────────────────────────────── */
const HORAS = Array.from({ length: 14 }, (_, i) => 8 + i); // 08-21
const DIAS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

/* lunes ISO de una fecha -------------------------------------- */
const isoMonday = (d: dayjs.Dayjs) => {
  const delta = (d.day() + 6) % 7; // 0 =Lun … 6 =Dom (conversión desde dayjs)
  return d.subtract(delta, "day");
};

/* ═════════════════════════════════════════════════════════════ */
export default function HeatmapHorarioReservasPage() {
  const { t, i18n } = useTranslation<"translation">();
  /* 1 · semana seleccionada */
  const [weekStart, setWeek] = useState(() => isoMonday(dayjs()));

  /* 2 · worker hook */
  const { run, useStatus } = useHeatmapHorarioReservasReport();
  const [jobId, setJobId] = useState<string>();
  const { data: status } = useStatus(jobId);

  // sincroniza locale (por si el tooltip o header usa idioma)
  useEffect(() => {
    dayjs.locale(i18n.language || "es");
  }, [i18n.language]);

  const fetchWeek = async (monday: dayjs.Dayjs) => {
    const desde = monday.format("YYYY-MM-DD");
    const hasta = monday.add(6, "day").format("YYYY-MM-DD");
    const { jobId } = await run({ desde, hasta });
    setJobId(jobId);
  };

  const handleDownload = async () => {
    if (status?.result?.url) {
      try {
        await downloadReportFile(status.result.url);
      } catch (error) {
        console.error("Error al descargar:", error);
      }
    }
  };

  /* primera carga */
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    fetchWeek(weekStart);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* navegación semana ±1 */
  const goWeek = (delta: number) => {
    const next = isoMonday(weekStart.add(delta, "week"));
    setWeek(next);
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    fetchWeek(next);
  };

  /* 3 · llenar matriz ABS */
  const matrix: number[][] = DIAS.map(() => HORAS.map(() => 0));
  (status?.result?.rows ?? []).forEach((r: HeatHorarioResRow) => {
    const row = Number(r.dow);
    const col = HORAS.indexOf(Number(r.hora));
    if (row >= 0 && row < DIAS.length && col !== -1) {
      matrix[row][col] = Number(r.abs ?? 0);
    }
  });

  const flat = matrix.flat();
  const maxAbs = Math.max(1, ...flat); // para color
  const totWeek = flat.reduce((s, v) => s + v, 0); // total semana

  const absToColor = (v: number) => {
    const h = (v / maxAbs) * 120; // 0→rojo, máx→verde
    return `hsl(${h}, 70%, 45%)`;
  };

  const minWidth = 80 + HORAS.length * 32;

  /* 4 · UI ------------------------------------------------------ */
  return (
    <Stack spacing={3}>
      {/* cabecera + navegación */}
      <Stack direction="row" spacing={1} alignItems="center">
        <IconButton onClick={() => goWeek(-1)} size="small">
          <ArrowBackIosIcon fontSize="small" />
        </IconButton>

        <Typography variant="h5" sx={{ textTransform: "capitalize" }}>
          {weekStart.format("DD MMM")} → {weekStart.add(6, "day").format("DD MMM YYYY")}
        </Typography>

        <IconButton onClick={() => goWeek(+1)} size="small">
          <ArrowForwardIosIcon fontSize="small" />
        </IconButton>
      </Stack>

      {/* progreso */}
      {status && !status.done && (
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={24} />
          <Typography>{t("heatmapReservas.estado.cargando")}</Typography>
        </Stack>
      )}

      {/* resultado */}
      {status?.done && status.result && (
        <>
          {status.result.url && (
            <Alert severity="success" sx={{ width: "fit-content" }}>
              XLSX:&nbsp;
              <Button
                variant="text"
                size="small"
                onClick={handleDownload}
                sx={{ textTransform: "none", textDecoration: "underline", p: 0, minWidth: "auto" }}
              >
                {t("heatmapReservas.estado.descargar")}
              </Button>
            </Alert>
          )}

          <Paper sx={{ p: 2, overflowX: "auto" }}>
            <Box sx={{ minWidth }}>
              {/* cabecera horas */}
              <Stack direction="row" spacing={0.5} sx={{ pl: 10, mb: 1 }}>
                {HORAS.map((h) => (
                  <Box key={h} sx={{ width: 32, textAlign: "center", fontSize: 12 }}>
                    {String(h).padStart(2, "0")}h
                  </Box>
                ))}
              </Stack>

              {/* grid día × hora */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: `80px repeat(${HORAS.length}, 32px)`,
                  gridAutoRows: "32px",
                  gap: 0.5,
                }}
              >
                {DIAS.map((dia, rowIdx) => (
                  <Box key={`row-${rowIdx}`} sx={{ display: "contents" }}>
                    {/* etiqueta día */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        pr: 1,
                        fontSize: 14,
                        fontWeight: 500,
                      }}
                    >
                      {dia}
                    </Box>

                    {/* celdas */}
                    {HORAS.map((h, colIdx) => {
                      const abs = matrix[rowIdx][colIdx];
                      return (
                        <Tooltip
                          key={`${dia}-${h}`}
                          title={t("heatmapReservas.tooltip", {
                            abs,
                            total: totWeek,
                          })}
                          arrow
                        >
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: 0.5,
                              bgcolor: absToColor(abs),
                              outline: "1px solid rgba(0,0,0,0.05)",
                            }}
                          />
                        </Tooltip>
                      );
                    })}
                  </Box>
                ))}
              </Box>
            </Box>
          </Paper>
        </>
      )}
    </Stack>
  );
}
